/// <reference path="../../../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />

import * as React from 'react';
import { AppProgressBar } from '../../components';
import { CancelClicked, GetTranscriptionFromExistingMedia, ProgressLabelFromLoadingStatus, LoadingStatus, SetForegroundTaskInStore, StatusLabelFromLoadingStatus, SetShowAuthPopup } from '../../actions/LoadingActions';
import { getLoadingPageStyles, getCancelLinkStyles, getStatusLabelContainerStyle } from './Loading.styles';
import loadingStore from '../../stores/LoadingStore';
import { Image, Link, ILink, Label } from 'office-ui-fabric-react';
import { RouteComponentProps } from 'react-router-dom';
import { ChangeEvent } from '../../stores/utils';
import { AppLayoutSettings } from '../common.styles';
import { SetFailureStateAndRouteToFailure, FailureContinuation } from '../../actions/FailureActions';
import { LogEvents } from '../../../utils/LogEvents';
import { RenderToPaneKeys } from '../telemetry/RenderToPaneKeys';
import { ErrorStringFromException } from '../../../utils/utils';
import { IsAuthError, IsUnrecoverableAuthError } from '../../../backend/office-js/auth/AuthErrors';
import { IForegroundTask } from '../../../backend/interfaces/IForegroundTask';
import { AppAuthenticationProviderContext } from '../../context';
import { DocumentStorageError, DocumentStorageErrors } from '../../../backend/office-js/DocumentStorageErrors';
import { FileUploadForegroundTaskError, FileUploadForegroundTaskErrors } from '../../../backend/FileUploadForegroundTaskErrors';
import { LogEventNames } from '../../../backend/interfaces/ITelemetryLogger';
import { ShouldLogErrorAsWarning } from '../../../utils/ErrorTelemetry';
import { AuthPopup } from '../../components/Popups/AuthPopup';
import { PerformGetAccessTokenCheckAndShowAuthPopupIfNeeded } from '../../actions/CommonActions';
import { AuthError, AuthErrors } from '../../../backend/office-js/auth/AuthErrors';

export interface LoadingState {
  /**
   * Contains a loading page user.
   */
  foregroundTask: IForegroundTask | null;

  /**
   * Keeps track of the current kind of loading task the Loading page is performing.
   */
  loadingStatus: LoadingStatus;

  /**
   * Percent of loading done or null if we are not showing progress indicator.
   */
  percentDone: number | null;

  /**
   * Shows a popup asking the user to verify identity for auth
   */
  showAuthPopup: boolean;
};

function GetFailureReasonFromError(error: Error, ) {
  if (error instanceof FileUploadForegroundTaskError && error.name == FileUploadForegroundTaskErrors.UnsupportedFileFormat)
    return TranscriptionStrings.UnsupportedUploadFileFormatFailureMessage;

  if (IsAuthError(error) || IsUnrecoverableAuthError(error)) {
    return TranscriptionStrings.AuthenticationLoadingFailureReason;
  }

  return TranscriptionStrings.UnknownLoadingFailureReason;
}

export class Loading extends React.PureComponent<RouteComponentProps, LoadingState> {
  static contextType = AppAuthenticationProviderContext;
  private cancelLinkRef = React.createRef<ILink>();
  private _isMounted: boolean;
  private authPopupPromiseFns: { resolve: () => void, reject: (e) => void } | null = null;

  constructor(props: RouteComponentProps) {
    super(props);
    this._isMounted = false;

    this.state = loadingStore.getState();
    loadingStore.on(ChangeEvent.Change, this._onStoreChanged);
  }

  public async componentDidMount() {
      this._isMounted = true;
      this.cancelLinkRef.current.focus();
      await this._performLoadingTasks();
  }

  public componentWillUnmount() {
    this._isMounted = false;
  }

  public render() {
    const styles = getLoadingPageStyles();
    const statusLabel = StatusLabelFromLoadingStatus(this.state.loadingStatus);
    const progressLabel = ProgressLabelFromLoadingStatus(this.state.loadingStatus);
    const percentDone = this.state.percentDone;
    return (
      <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
        <div style={getStatusLabelContainerStyle()}>
          <Label styles={AppLayoutSettings.AppCommonStyles.getBoldLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx,AppLayoutSettings.AppColors.mediumLabelColor)}>{statusLabel}</Label>
          <Label styles={AppLayoutSettings.AppCommonStyles.getBoldLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx,AppLayoutSettings.AppColors.mediumLabelColor)}>{ percentDone != null ? Math.floor(percentDone * 100) + '%' : null }</Label> 
        </div>
          <div className={styles.progressContainer}>
          <AppProgressBar type={'AppProgressBarProps'} description={progressLabel} ariaValueText={progressLabel} barHeight={3} percentComplete={percentDone} />
        </div>
        <div className={styles.cancelLinkContainer}>
          <Link role="button" componentRef={this.cancelLinkRef} styles={getCancelLinkStyles()} onClick={() => { this._onCancelClicked() }} href="#">{TranscriptionStrings.LoadingPageCancelButtonLabel}</Link>
        </div>
        <div className={styles.imageContainer}>
          <Image alt="" src={require('../../../../assets/' + AppLayoutSettings.AppImageParams.cartoonImageName)} width={AppLayoutSettings.AppImageParams.cartoonImageWidthPx} height={AppLayoutSettings.AppImageParams.cartoonImageHeightPx} />
        </div>
        {   
          this.state.showAuthPopup ?
          <AuthPopup
              onAuthSucceeded={() => this._onAuthSucceeded() } 
              onAuthFailed={(e) => this._onAuthFailed(e) }
              onDialogClosed={() => this._onAuthFailed() } />
          : null
        }
      </div>
    );
  }

  private async _onAuthSucceeded() {
    SetShowAuthPopup(false /*showAuthPopup*/);
    this.authPopupPromiseFns.resolve();
    this.authPopupPromiseFns = null;
  }

  private _onAuthFailed(e?) {
    SetShowAuthPopup(false /*showAuthPopup*/);
    this.authPopupPromiseFns.reject(new AuthError(AuthErrors.AuthFailedInAuthPopup, e ? ErrorStringFromException(e) : undefined));
    this.authPopupPromiseFns = null;
  }

  private _onCancelClicked() {
    try {
      CancelClicked(this.props.history, this.state.foregroundTask);
      LogEvents.RenderToPane.End([{ key: RenderToPaneKeys.RenderFailureReason, value: "User canceled." }, { key: RenderToPaneKeys.RenderedSuccessfully, value: false }]);
    } catch (error) {
      this._handleError(error, "Canceling threw error", FailureContinuation.StartOver, GetFailureReasonFromError(error));
    }
  };

  private async _performLoadingTasks() {
    if (this.state.foregroundTask != null) {
      try {
        await this.state.foregroundTask.Execute(new Promise(async (resolve, reject) => {
          try {
            const authSuccessful = await PerformGetAccessTokenCheckAndShowAuthPopupIfNeeded(this.context, SetShowAuthPopup);
            this.authPopupPromiseFns = { resolve, reject };
  
            if (authSuccessful) {
              this.authPopupPromiseFns.resolve();
              this.authPopupPromiseFns = null;
              return;
            }
          } catch (e) {
            this.authPopupPromiseFns = null;
            reject(e);
          }
        }));
      } catch (e) {
        SetForegroundTaskInStore(null);
        const failureMessage: string = IsAuthError(e) ? TranscriptionStrings.AuthRecoveringFailureReason : GetFailureReasonFromError(e);
        this._handleError(e, "Upload and fetch threw error", IsUnrecoverableAuthError(e) ? FailureContinuation.None : FailureContinuation.Retry, failureMessage);
      }
    } else {
      try {
        await GetTranscriptionFromExistingMedia(this.context, this.props.history);
      } catch (error) {
        let continuation:FailureContinuation = FailureContinuation.Retry;
        if (error instanceof DocumentStorageError && error.name == DocumentStorageErrors.CorruptTranscriptSource) {
          continuation = FailureContinuation.StartOver;
        }

        this._handleError(error, "Fetch threw error", continuation, GetFailureReasonFromError(error));
      }
    }
  }

  private _handleError(error, source: string, failureContinuation: FailureContinuation, failureReasonForUser: string) {
    let failureArea = LogEventNames.RenderToPane;
    let renderFailureReason = source + ". " + ErrorStringFromException(error);

    if (ShouldLogErrorAsWarning(error)) {
      failureArea = LogEventNames.Warning;
      renderFailureReason = "Render failed with warning";
    }

    LogEvents.RenderToPane.End([{ key: RenderToPaneKeys.RenderedSuccessfully, value: false }, { key: RenderToPaneKeys.RenderFailureReason, value: renderFailureReason }]);
    SetFailureStateAndRouteToFailure(failureArea, failureReasonForUser, failureContinuation, this.props.history);
  }

  private _onStoreChanged = (): void => {
    if (this._isMounted) {
      this.setState(loadingStore.getState());
    }
  };
}